import { Injectable } from '@angular/core';

import { environment } from '../../environments/environment';
import { Credential } from '../helpers/user';

import { AuthService } from './auth.service';
import { UserService } from './user.service';

@Injectable({
    providedIn: 'root'
})
export class SnippetService {

    constructor(
        private authService: AuthService,
        private userService: UserService,
    ) { }

    /**
     * Inserts credential values into snippet placeholders.
     * @param block HTML element to add credentials to.
     */
    private addCredentials(block: HTMLElement): void {
        const authorized: boolean = this.authService.isAuthorized();
        const credentials: Array<Credential> = this.userService.getCredentials();

        const template: string = block.innerHTML;
        const credentialWrapper: HTMLDivElement = document.createElement('div');
        credentialWrapper.innerHTML = document.getElementById(environment.snippetCredentialTemplateId).innerHTML;

        const credentialMap: Map<string, string> = new Map<string, string>();
        for (const credential of credentials) {
            credentialMap.set(credential.key, credential.value);
        }

        const code: string = template.replace(/\{\{[^\}]+\}\}/g, (match: string): string => {
            const key: string = match.slice(2, -2);
            let body: string;
            let modifier: string;
            let tooltip: string;

            if (!authorized) {
                body = `{{${key}}}`;
                modifier = 'unauthorized';
                tooltip = environment.snippetCredentialTooltips.unauthorized;
            } else if (credentialMap.has(key)) {
                body = credentialMap.get(key);
                modifier = 'added';
                tooltip = environment.snippetCredentialTooltips.added;
            } else {
                body = `{{${key}}}`;
                modifier = 'authorized';
                tooltip = environment.snippetCredentialTooltips.authorized;
            }

            const credentialButton: HTMLButtonElement = credentialWrapper.querySelector(`.${environment.snippetCredentialClass}`);
            credentialButton.classList.add(`${environment.snippetCredentialClass}--${modifier}`);

            const credentialBody: HTMLElement = credentialWrapper.querySelector(`.${environment.snippetCredentialClass}__body`);
            credentialBody.innerHTML = body;

            const credentialTooltip: HTMLElement = credentialWrapper.querySelector(`.${environment.snippetCredentialClass}__tooltip-body`);
            credentialTooltip.innerHTML = tooltip;

            return credentialWrapper.innerHTML;
        });

        block.innerHTML = code;
    }

    /**
     * Cleans up snippet content.
     * @param code Snippet content.
     */
    cleanup(code: string): string {
        return code.replace(/<br>/g, '\n').replace(/&nbsp;/g, ' ').replace(/^\n*/, '');
    }

    /**
     * Finds closest (target or parent) snippet element for the event provided.
     * @param event DOM event object.
     */
    private getCredentialElement(event: Event): Element {
        return (<Element>event.target).closest(`.${environment.snippetCredentialClass}`);
    }

    /**
     * Handles snippet click event.
     * @param event Click event.
     */
    handleClick(event: Event): void {
        const snippetCredential: Element = this.getCredentialElement(event);

        if (snippetCredential) {
            if (snippetCredential.classList.contains(`${environment.snippetCredentialClass}--unauthorized`)) {
                this.authService.login();
            } else if (snippetCredential.classList.contains(`${environment.snippetCredentialClass}--authorized`)) {
                this.userService.openCredentials();
            }
        }
    }

    /**
     * Handles snippet mouse over event.
     * @param event Mouse over event.
     */
    handleOver(event: Event): void {
        const snippetCredential: Element = this.getCredentialElement(event);

        if (snippetCredential) {
            const snippetRect: ClientRect = snippetCredential.getBoundingClientRect();
            const tooltip: HTMLElement = snippetCredential.querySelector(`.${environment.snippetCredentialClass}__tooltip`);
            const tooltipRect: ClientRect = tooltip.getBoundingClientRect();

            const left: number = snippetRect.left + (snippetRect.width / 2) - (tooltipRect.width / 2);
            const top: number = snippetRect.top > tooltipRect.height ? snippetRect.top - tooltipRect.height - 1 : snippetRect.bottom + 1;
            const maxLeft: number = document.body.offsetWidth - tooltipRect.width;

            tooltip.style.left = `${Math.max(Math.min(left, maxLeft), 0)}px`;
            tooltip.style.top = `${top}px`;
        }
    }

    /**
     * Initializes snippet.
     * @param host Snippet component element.
     * @param selector Snippet container selector.
     * @param content Snippet content.
     */
    initSnippet(host: HTMLElement, selector: string, content: string): Promise<void> {
        let resolvePromise: () => void;
        const promise: Promise<void> = new Promise<void>((resolve: () => void) => {
            resolvePromise = resolve;
        });

        // let Angular render DOM firstly
        setTimeout(() => {
            const block: HTMLElement = host.querySelector(selector);
            block.innerHTML = content;

            // highlight code
            window.hljs.highlightBlock(block);
            // add line numbers
            //window.hljs.lineNumbersBlock(block);

            setTimeout(() => {
                // add credentials
                this.addCredentials(block);

                resolvePromise();
            });
        });

        return promise;
    }
}
